<template>
  <v-form>
    <v-container
      v-show="availableBrands.length > 1 || !userIsLoggedIn"
      :class="$style['brand-container']"
    >
      <v-row>
        <v-col :class="$style['brand-heading']"
          ><span :class="$style['required-indicator']">* </span> {{ $t('contact.field.brand') }}
        </v-col>
      </v-row>
      <v-row>
        <v-col :class="$style['brand-selection']">
          <v-radio-group
            v-model="selectedBrand"
            row
            :hide-details="false"
            :error-messages="selectedBrandErrors"
            :disabled="brandIsPrefilled"
          >
            <v-radio
              v-for="radio in availableBrands"
              :key="radio.value"
              class="mb-4 mr-8"
              :value="radio.value"
            >
              <template #label
                ><BrandLogoRef :brand="radio.brandPimId" :class="$style.brand" :title="radio.label"
              /></template>
            </v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
    </v-container>
    <v-row class="flex-sm-row flex-column">
      <v-col>
        <v-text-field
          v-model="email"
          outlined
          class="requiredHubspotFormInput"
          :label="$t('hubspot.formField.email')"
          :error-messages="emailErrors"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          v-model="mobilePhone"
          outlined
          :label="$t('hubspot.formField.mobilephone')"
        ></v-text-field>
      </v-col>

      <v-col>
        <v-text-field
          v-model="phoneNumber"
          outlined
          :label="$t('hubspot.formField.phone')"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="lastName"
          outlined
          class="requiredHubspotFormInput"
          :label="$t('hubspot.formField.lastname')"
          :error-messages="lastNameErrors"
        ></v-text-field>
      </v-col>

      <v-col>
        <v-text-field
          v-model="firstName"
          outlined
          class="requiredHubspotFormInput"
          :label="$t('hubspot.formField.firstname')"
          :error-messages="firstNameErrors"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="customSubject"
          outlined
          :label="$t('hubspot.formField.customSubject')"
          :error-messages="customSubjectErrors"
          maxlength="150"
          counter
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- v-if used to fill automatic if value in store, else user not logged in and fill manual -->
    <v-row v-if="!userIsLoggedIn">
      <v-col>
        <v-select
          v-model="countryId"
          class="requiredHubspotFormInput"
          :error-messages="countryIdErrors"
          :items="countries"
          :label="$t('hubspot.formField.country')"
          outlined
          required
        ></v-select>
      </v-col>

      <v-col>
        <v-select
          v-model="languageId"
          :items="languages"
          :label="$t('hubspot.formField.language')"
          class="requiredHubspotFormInput"
          :error-messages="languageIdErrors"
          outlined
          required
        ></v-select>
      </v-col>

      <v-col>
        <v-text-field
          v-model="customerId"
          outlined
          class="requiredHubspotFormInput"
          :label="$t('hubspot.formField.customerId')"
          :error-messages="customerIdErrors"
        ></v-text-field>
      </v-col>
    </v-row>

    <template v-if="serialNumber">
      <JumpToExternalForm :jump-to-page-keys="jumpToPageKeys" />
    </template>
  </v-form>
</template>

<script>
import JumpToExternalForm from '@/js/components/HubSpot/JumpToExternalForm.vue'
import HubspotScriptMixin from '@/js/mixins/hubspot-script-mixin.js'
import { camelCase } from 'change-case'

import BrandLogoRef from '@/js/components/Shared/BrandLogoRef.vue'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import { required, email, maxLength } from 'vuelidate/lib/validators'

import { mapGetters } from 'vuex'
import { types as STORE_MODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'

export default {
  name: 'BaseForm',
  components: { JumpToExternalForm, BrandLogoRef },
  mixins: [FormValidationMixin, HubspotScriptMixin],
  props: {
    prefillFirstName: {
      type: String,
      default: '',
    },
    prefillLastName: {
      type: String,
      default: '',
    },
    prefillEmail: {
      type: String,
      default: '',
    },
    prefillMobilePhone: {
      type: String,
      default: '',
    },
    prefillPhoneNumber: {
      type: String,
      default: '',
    },
    prefillSubject: {
      type: String,
      default: '',
    },
    prefillSerialNumber: {
      type: String,
      default: '',
    },
    prefillBrand: {
      type: String,
      default: '',
    },
    userIsLoggedIn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstName: this.prefillFirstName,
      lastName: this.prefillLastName,
      email: this.prefillEmail,
      countryId: '',
      languageId: '',
      customerId: '',
      userId: '',
      customSubject: this.prefillSubject,
      mobilePhone: this.prefillMobilePhone,
      phoneNumber: this.prefillPhoneNumber,
      serialNumber: this.prefillSerialNumber,
      selectedBrand: this.filteredPrefillSelectedBrand,
      fallbackDefaultBrand: 'KALKHOFF',
      jumpToPageKeys: {
        'service-warranty-battery': '_blank',
        engineComplaintFormUrl: '_blank',
        'service-warranty-return': '_blank',
      },
    }
  },
  validations: {
    email: {
      required,
      email,
    },
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    countryId: {
      required,
    },
    languageId: {
      required,
    },
    customerId: {
      required,
      maxLength: maxLength(6),
    },
    customSubject: {
      maxLength: maxLength(150),
    },
    selectedBrand: {
      required,
    },
  },
  computed: {
    countries() {
      return [
        { text: this.$t('hubspot.formField.countries.austria'), value: 'AU' },
        { text: this.$t('hubspot.formField.countries.belgium'), value: 'BE' },
        { text: this.$t('hubspot.formField.countries.czechRepublic'), value: 'CZ' },
        { text: this.$t('hubspot.formField.countries.denmark'), value: 'DK' },
        { text: this.$t('hubspot.formField.countries.france'), value: 'FR' },
        { text: this.$t('hubspot.formField.countries.germany'), value: 'DE' },
        { text: this.$t('hubspot.formField.countries.luxembourg'), value: 'LU' },
        { text: this.$t('hubspot.formField.countries.netherlands'), value: 'NL' },
        { text: this.$t('hubspot.formField.countries.norway'), value: 'NO' },
        { text: this.$t('hubspot.formField.countries.poland'), value: 'PL' },
        { text: this.$t('hubspot.formField.countries.slovakia'), value: 'SK' },
        { text: this.$t('hubspot.formField.countries.spain'), value: 'ES' },
        { text: this.$t('hubspot.formField.countries.switzerland'), value: 'CH' },
        { text: this.$t('hubspot.formField.countries.unitedKingdom'), value: 'UK' },
      ]
    },
    languages() {
      return [
        { text: this.$t('hubspot.formField.languages.dutch'), value: 'nl' },
        { text: this.$t('hubspot.formField.languages.english'), value: 'en' },
        { text: this.$t('hubspot.formField.languages.french'), value: 'fr' },
        { text: this.$t('hubspot.formField.languages.german'), value: 'de' },
        { text: this.$t('hubspot.formField.languages.polish'), value: 'pl' },
        { text: this.$t('hubspot.formField.languages.spanish'), value: 'es' },
        { text: this.$t('hubspot.formField.languages.spanish'), value: 'cs' },
      ]
    },
    brandIsPrefilled() {
      return this.prefillBrand !== ''
    },
    filteredPrefillSelectedBrand() {
      if (this.brandsContainPrefillBrand(this.prefillBrand)) {
        return this.prefillBrand
      } else {
        if (this.brandIsPrefilled) {
          return this.fallbackDefaultBrand
        } else {
          return null
        }
      }
    },
    ...mapGetters(STORE_MODULES.AIMEOS, {
      customerCountryId: APP_GETTER.CUSTOMER_COUNTRYID,
      userFormData: 'CUSTOMER_USER_FORM_DATA',
    }),
    ...mapGetters(STORE_MODULES.APP, {
      customerUser: APP_GETTER.USER,
      customerUserId: APP_GETTER.CUSTOMER_NUMBER,
      customerLanguageId: APP_GETTER.CURRENT_LANG_CODE,
    }),
    availableBrands() {
      return this.brands.map(brand => {
        return {
          value: brand.toUpperCase(),
          label: this.$t(`filter.items.ItemBrand.title.${brand}`),
          brandPimId: this.$t(`settings.brand.nameToPimId.${camelCase(brand).toLowerCase()}`),
        }
      })
    },
    emailErrors() {
      return this.formErrorHelper('email', [this.requiredError, this.emailError])
    },
    firstNameErrors() {
      return this.formErrorHelper('firstName', [this.requiredError])
    },
    lastNameErrors() {
      return this.formErrorHelper('lastName', [this.requiredError])
    },
    countryIdErrors() {
      return this.formErrorHelper('countryId', [this.requiredError])
    },
    languageIdErrors() {
      return this.formErrorHelper('languageId', [this.requiredError])
    },
    customerIdErrors() {
      return this.formErrorHelper('customerId', [this.maxLengthError(6), this.requiredError])
    },
    customSubjectErrors() {
      return this.formErrorHelper('customsubject', [this.maxLengthError(150)])
    },
    selectedBrandErrors() {
      return this.formErrorHelper('selectedBrand', [this.requiredError])
    },
    postData() {
      const data = {
        fields: [
          {
            objectTypeId: '0-1', // 0-1 für Contact Properties, 0-5 für Ticket Properties in Hubspot
            name: 'firstname', // MUSS identisch mit dem Namen des Felds des Formulars in Hubspot sein
            value: this.firstName, // Wert der übergeben werden soll
          },
          {
            objectTypeId: '0-1',
            name: 'lastname',
            value: this.lastName,
          },
          {
            objectTypeId: '0-1',
            name: 'email',
            value: this.email,
          },
          {
            objectTypeId: '0-1',
            name: 'mobilephone',
            value: this.mobilePhone,
          },
          {
            objectTypeId: '0-1',
            name: 'phone',
            value: this.phoneNumber,
          },
          {
            objectTypeId: '0-1',
            name: 'country',
            value: this.countryId,
          },
          {
            objectTypeId: '0-5',
            name: 'language',
            value: this.languageId,
          },
          {
            objectTypeId: '0-1',
            name: 'client_number',
            value: this.userId,
          },
          {
            objectTypeId: '0-1',
            name: 'customsubject',
            value: this.customSubject,
          },
          {
            objectTypeId: '0-5',
            name: 'client_number',
            value: this.customerId,
          },
        ],
      }

      if (this.serialNumber) {
        const serialNumberFields = {
          objectTypeId: '0-5',
          name: 'serial_number',
          value: this.serialNumber,
        }

        data.fields.push(serialNumberFields)
      }

      return { data }
    },
    customerDataFieldsToWatch() {
      // only way for VUE2 to watch multiple data, replace undefined to return truely undefined datatype
      return `${this.userFormData?.firstname}${this.userFormData?.lastname}${this.userFormData?.email}${this.userFormData?.mobile}${this.userFormData?.telephone}`.replaceAll(
        'undefined',
        '',
      )
    },
  },
  watch: {
    // This indicates that the customer request is finished thus we have customer data
    customerDataFieldsToWatch: {
      handler(nextProps, prevProps) {
        if (nextProps) {
          // only refresh after first initial load
          const forceRefresh = !!prevProps
          this.setPrefillsFromStore(forceRefresh)
          if (!forceRefresh) {
            this.$emit('load-from-store-complete')
          }
        }
      },
      immediate: true,
    },
    selectedBrand: {
      handler() {
        this.$emit('set-selected-brand', this.selectedBrand)
      },
      immediate: true,
    },
    filteredPrefillSelectedBrand: {
      handler() {
        this.selectedBrand = this.filteredPrefillSelectedBrand
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.availableBrands.length == 1) {
      this.selectedBrand = this.availableBrands[0].value
    }
    if (!this.userIsLoggedIn) {
      this.$emit('load-from-store-complete')
    }
  },
  methods: {
    brandsContainPrefillBrand(brandToCheck) {
      return this.availableBrands.some(entry => entry.value === brandToCheck)
    },
    setPrefillsFromStore(forceRefresh = false) {
      this.countryId = this.customerCountryId
      this.languageId = this.customerLanguageId
      this.userId = this.customerUserId
      if (this.customerUser?.parentCustomer) {
        // Customer is Subuser so return parent ID
        this.customerId = this.customerUser.parentCustomer
      } else {
        // Customer is User so use User ID
        this.customerId = this.customerUserId
      }
      // Only set Prefill from Store if not in URL
      if (!this.email || forceRefresh) {
        this.email = this.userFormData.email
      }
      if (!this.firstName || forceRefresh) {
        this.firstName = this.userFormData.firstname
      }
      if (!this.lastName || forceRefresh) {
        this.lastName = this.userFormData.lastname
      }
      if (!this.mobilePhone || forceRefresh) {
        this.mobilePhone = this.userFormData.mobile
      }
      if (!this.phoneNumber || forceRefresh) {
        this.phoneNumber = this.userFormData.telephone
      }
    },
    setSelectedBrand(brand) {
      this.selectedBrand = brand
    },
  },
}
</script>
<style lang="scss" module>
.brand {
  order: 1;
  // width: 45%;
  width: 9rem;
}

.brand-container {
  margin-bottom: 24px;
  padding-top: 12px !important;
  padding-bottom: 0 !important;

  border: 1px solid #819ea8;
}

.brand-heading {
  padding-bottom: 0;
}

.brand-selection {
  padding-top: 0;

  :global(.v-input__slot) {
    margin-bottom: 0;
  }
}

.required-indicator {
  color: #e03537;
}
</style>
